<template>
  <PPage :title="$t('sidebar.transferencias')" icon="IconTransfers">
    <POptionBarNew v-model="optionSelected" :options="options" class="option-bar">
      <template #header>
        <PActionsFloat :actions="actions" @click="actionModal" />
      </template>
      <template #propias>
        <TransferenciasPropias
          v-if="reseteador && propiasOfertasConfig && propiasDemandasConfig"
          :propiasOfertasConfig="propiasOfertasConfig"
          :propiasDemandasConfig="propiasDemandasConfig"
          :transportadorasOptions="transportadoras"
          @editarOfertaOrDemanda="onEditarOfertaOrDemanda"
        />
      </template>
      <template #publicas>
        <TransferenciasPublicas
          v-if="reseteador && publicasOfertasConfig && publicasDemandasConfig"
          :publicasOfertasConfig="publicasOfertasConfig"
          :publicasDemandasConfig="publicasDemandasConfig"
          :transportadorasOptions="transportadoras"
        />
      </template>
    </POptionBarNew>
    <PModal
      :show="verModal"
      @close="onCloseModal"
      :closeClickExit="false"
      :title="titleModal"
      :width="verImportarForm ? '1000' : ''"
    >
      <OfertarForm
        v-if="verOfertarForm"
        @action="action"
        :action-source="actionSource"
        :ofertaDemanda="ofertaDemandaEdit"
      />
      <DemandarForm
        v-if="verDemandarForm"
        @action="action"
        :action-source="actionSource"
        :ofertaDemanda="ofertaDemandaEdit"
      />
      <ImportarForm
        v-if="verImportarForm"
        @cancel="verModal = false"
        @action="action"
        :options-paises="optionsPaises"
        :textActionName="textActionName"
        action-name="importar"
      ></ImportarForm>
    </PModal>
  </PPage>
</template>
<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import TransferenciasPropias from "./propias/TransferenciasPropias.vue";
import TransferenciasPublicas from "./publicas/TransferenciasPublicas.vue";
import { Action, Getter } from "vuex-class";
import { IColumnaResponse, IDemandaParams, IOfertaParams } from "@/services";
import OfertarForm from "./common/OfertarForm.vue";
import DemandarForm from "./common/DemandarForm.vue";
import { EventBus } from "@/event-bus";
import {
  ActionOnOfertaDemanda,
  ICliente,
  IDemanda,
  IOferta,
  IOfertaDemanda,
  IPais,
  ITransportadora,
} from "@/entities";
import ImportarForm from "@/pages/transferencias/common/ImportarForm.vue";
import VueMethods from "@/vue-methods";
import { IOptionBarItem } from "@/components/look";
import { ETransferenciaType } from "./types";

@Component({
  mixins: [VueMethods],
  components: {
    ImportarForm,
    TransferenciasPropias,
    TransferenciasPublicas,
    OfertarForm,
    DemandarForm,
  },
})
export class TransferenciasPage extends Vue {
  @Action("saveDemanda") saveDemanda!: (params?: IDemandaParams) => Promise<IDemanda | undefined>;
  @Action("updateDemanda") updateDemanda!: (
    params?: IDemandaParams
  ) => Promise<IDemanda | undefined>;
  @Action("fetchColumna") fetchColumna!: (
    codTabla: string
  ) => Promise<IColumnaResponse | undefined>;
  @Action("fetchTiposCoste") fetchTiposCoste!: () => Promise<void>;
  @Action("saveOferta") saveOferta!: (params?: IOfertaParams) => Promise<IOferta | undefined>;
  @Action("updateOferta") updateOferta!: (params?: IOfertaParams) => Promise<IOferta | undefined>;
  @Getter("getPaises") getPaises!: IPais[];
  @Getter("getClienteSelected") clienteSelected!: ICliente;

  verModal = false;
  titleModal = "";
  verOfertarForm = false;
  verDemandarForm = false;
  verImportarForm = false;
  optionsPaises: IPais[] = [];
  transportadorasOptions: ITransportadora[] = [];
  textActionName = this.$t("label.importar");
  actions = [
    { action: "ofertar", label: this.$t("label.ofertar") },
    { action: "demandar", label: this.$t("label.demandar") },
  ];

  public propiasOfertasConfig: IColumnaResponse | null = null;
  public propiasDemandasConfig: IColumnaResponse | null = null;
  public publicasOfertasConfig: IColumnaResponse | null = null;
  public publicasDemandasConfig: IColumnaResponse | null = null;
  public actionSource: string | null = null;
  public ofertaDemandaEdit: IOfertaDemanda | null = null;

  async created(): Promise<void> {
    const responses = await Promise.all([
      this.fetchColumna("propiasOfertas"),
      this.fetchColumna("propiasDemandas"),
      this.fetchColumna("publicasOfertas"),
      this.fetchColumna("publicasDemandas"),
      this.fetchTiposCoste(),
    ]);

    this.propiasOfertasConfig = responses[0] || {
      codTabla: "propiasOfertas",
      codColumna: "",
    };
    this.propiasDemandasConfig = responses[1] || {
      codTabla: "propiasDemandas",
      codColumna: "",
    };
    this.publicasOfertasConfig = responses[2] || {
      codTabla: "publicasOfertas",
      codColumna: "",
    };
    this.publicasDemandasConfig = responses[3] || {
      codTabla: "publicasDemandas",
      codColumna: "",
    };
  }
  mounted(): void {
    this.optionsPaises = this.getPaises.filter((pais) => pais.importCheck);
    if (this.optionsPaises.length) {
      this.actions.push({ action: "importar", label: this.$t("label.importar") });
    }
    this.searchTransportadoras();
  }

  get optionSelected(): string {
    return this.$route.name || "propias";
  }

  set optionSelected(option: string) {
    this.$router.push({ path: `/transferencias/${option}` });
  }

  get options(): IOptionBarItem[] {
    return [
      {
        text: this.$t("label.propias"),
        value: "propias",
      },
      {
        text: this.$t("label.publicas"),
        value: "publicas",
      },
    ];
  }

  get transportadoras() {
    let transportadoraList: ITransportadora[] = [];
    if (this.clienteSelected?.pais.hasTransportadora) {
      this.transportadorasOptions.forEach((transportadora) => {
        if (
          transportadora.active &&
          transportadora.pais.codPaisCorto == this.clienteSelected.pais.codPaisCorto
        )
          transportadoraList.push(transportadora);
      });
    }
    return transportadoraList.sort((a, b) => this.sortAscending(a, b, "descripcion"));
  }

  async searchTransportadoras(): Promise<void> {
    const res = await this.$services.transportadoras.fetchAllTransportadoras();
    if (res) {
      this.transportadorasOptions = res;
    }
  }

  actionModal(action: string): void {
    if (action == "ofertar") {
      this.setupOfertaModal();
    } else if (action == "demandar") {
      this.setupDemandaModal();
    } else if (action == "importar") {
      this.setupImportarModal();
    }
  }

  reseteador = true; //Es para recargar datos despues de crear ofertas o demandas siempre tiene que terminar a true
  async action(action: { action: string; data?: any }): Promise<void> {
    if (action.action == "importConfirm") {
      this.reseteador = false;
      const res = await this.$services.transferencias.importTransferencias(action.data);
      if (res) {
        this.downloadFile(res);
        this.verModal = false;
      }
      this.reseteador = true;
    } else {
      if (action.action == "ofertarConfirm") {
        this.reseteador = false;
        const res =
          this.actionSource === ActionOnOfertaDemanda.EDITAR
            ? await this.updateOferta(action.data)
            : await this.saveOferta(action.data);
        if (res) {
          this.onCloseModalSuccess(ETransferenciaType.OFERTA);
        } else {
          EventBus.$emit("p-toast", { variant: "danger", message: res });
        }
        this.reseteador = true;
      } else if (action.action == "demandarConfirm") {
        this.reseteador = false;
        const res =
          this.actionSource === ActionOnOfertaDemanda.EDITAR
            ? await this.updateDemanda(action.data)
            : await this.saveDemanda(action.data);

        if (res) {
          this.onCloseModalSuccess(ETransferenciaType.DEMANDA);
        } else {
          EventBus.$emit("p-toast", { variant: "danger", message: res });
        }
        this.reseteador = true;
      }
    }
  }

  onEditarOfertaOrDemanda({ type, data }: { type: string; data: IOfertaDemanda }): void {
    this.actionSource = ActionOnOfertaDemanda.EDITAR;
    this.ofertaDemandaEdit = data;

    if (type === "OFERTA") {
      this.setupOfertaModal();
    } else if (type === "DEMANDA") {
      this.setupDemandaModal();
    }
  }

  private setupOfertaModal(): void {
    this.verModal = true;
    this.titleModal = this.$t("label.ofertar");
    this.verImportarForm = false;
    this.verDemandarForm = false;
    this.verOfertarForm = true;
  }

  private setupDemandaModal(): void {
    this.verModal = true;
    this.titleModal = this.$t("label.demandar");
    this.verImportarForm = false;
    this.verOfertarForm = false;
    this.verDemandarForm = true;
  }

  private setupImportarModal(): void {
    this.verModal = true;
    this.titleModal = this.$t("label.importar");
    this.verOfertarForm = false;
    this.verDemandarForm = false;
    this.verImportarForm = true;
  }

  private onCloseModal(): void {
    this.verModal = false;
    if (this.actionSource === ActionOnOfertaDemanda.EDITAR) {
      this.ofertaDemandaEdit = null;
    }
    this.actionSource = "";
  }

  private onCloseModalSuccess(type: ETransferenciaType): void {
    this.verModal = false;
    let message = "";
    if (type === ETransferenciaType.OFERTA) {
      message =
        this.actionSource === ActionOnOfertaDemanda.EDITAR
          ? "label.oferta.actualizada"
          : "label.oferta.guardada";
    } else if (type === ETransferenciaType.DEMANDA) {
      message =
        this.actionSource === ActionOnOfertaDemanda.EDITAR
          ? "label.demanda.actualizada"
          : "label.demanda.guardada";
    }
    EventBus.$emit("p-toast", {
      variant: "success",
      message: this.$t(message),
    });
    this.actionSource = "";
  }
}
export default TransferenciasPage;
</script>
<style lang="scss" scoped>
.option-bar::v-deep {
  & > .p-option-bar-container {
    padding-top: 0;
  }

  @media (min-width: 681px) {
    & > .p-option-bar-container {
      margin-top: -1em !important;
      padding-bottom: 2rem !important;
    }
  }
}
</style>
